import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Link,
} from '@mui/material';
import {
  Security as SecurityIcon,
  Storage as StorageIcon,
  Gavel as GavelIcon,
  Email as EmailIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';

// ============================================================================
// PRIVACY POLICY PAGE
// Comprehensive privacy policy for GDPR and Australian Privacy Act compliance
// ============================================================================

const PrivacyPolicy: React.FC = () => {
  const lastUpdated = 'December 8, 2025';
  const companyName = 'Centre Of Business';
  const appName = 'Check Set Go';
  const contactEmail = 'support@checksetgo.com';
  const websiteUrl = 'https://checksetgo.com';

  // Section component for consistent styling
  const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h5" sx={{ mb: 2, color: 'primary.main', fontWeight: 600 }}>
        {title}
      </Typography>
      {children}
    </Box>
  );

  // Subsection component
  const SubSection: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600 }}>
        {title}
      </Typography>
      {children}
    </Box>
  );

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={0} sx={{ p: { xs: 3, md: 6 }, borderRadius: 3, border: '1px solid #E0E0E0' }}>
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <SecurityIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
            <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
              Privacy Policy
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Last updated: {lastUpdated}
            </Typography>
          </Box>

          <Divider sx={{ mb: 4 }} />

          {/* Introduction */}
          <Section title="1. Introduction">
            <Typography paragraph>
              {companyName} ("we", "us", or "our") operates the {appName} platform (the "Service"). 
              This Privacy Policy explains how we collect, use, disclose, and safeguard your information 
              when you use our Service.
            </Typography>
            <Typography paragraph>
              We are committed to protecting your privacy and complying with the Australian Privacy Act 1988 
              (Cth), the Australian Privacy Principles (APPs), and the European Union General Data Protection 
              Regulation (GDPR) where applicable.
            </Typography>
            <Typography paragraph>
              By using our Service, you agree to the collection and use of information in accordance with 
              this policy. If you do not agree with this policy, please do not use our Service.
            </Typography>
          </Section>

          {/* Information We Collect */}
          <Section title="2. Information We Collect">
            <SubSection title="2.1 Information You Provide">
              <Typography paragraph>
                We collect information that you voluntarily provide when using our Service:
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                  <ListItemText 
                    primary="Account Information" 
                    secondary="Name, email address, password, company name, job title"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                  <ListItemText 
                    primary="Business Information" 
                    secondary="Company details, industry, employee count, location"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                  <ListItemText 
                    primary="Review Data" 
                    secondary="Review responses, action items, comments, and insights"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                  <ListItemText 
                    primary="Payment Information" 
                    secondary="Billing details processed securely through Stripe (we do not store card numbers)"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                  <ListItemText 
                    primary="Communications" 
                    secondary="Messages you send to us, feedback, and support requests"
                  />
                </ListItem>
              </List>
            </SubSection>

            <SubSection title="2.2 Information Collected Automatically">
              <Typography paragraph>
                When you access our Service, we automatically collect:
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                  <ListItemText 
                    primary="Usage Data" 
                    secondary="Pages visited, features used, time spent, click patterns"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                  <ListItemText 
                    primary="Device Information" 
                    secondary="Browser type, operating system, device type, screen resolution"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                  <ListItemText 
                    primary="Log Data" 
                    secondary="IP address, access times, referring URLs, error logs"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                  <ListItemText 
                    primary="Cookies" 
                    secondary="Session cookies for authentication and preferences (see our Cookie Policy)"
                  />
                </ListItem>
              </List>
            </SubSection>
          </Section>

          {/* How We Use Your Information */}
          <Section title="3. How We Use Your Information">
            <Typography paragraph>
              We use the information we collect for the following purposes:
            </Typography>
            
            <SubSection title="3.1 Provide and Maintain the Service">
              <List dense>
                <ListItem>
                  <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                  <ListItemText primary="Create and manage your account" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                  <ListItemText primary="Process audit sessions and generate analysis" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                  <ListItemText primary="Manage action items and sub-tasks" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                  <ListItemText primary="Process payments and subscriptions" />
                </ListItem>
              </List>
            </SubSection>

            <SubSection title="3.2 AI-Powered Features">
              <Typography paragraph>
                Our Service uses artificial intelligence to enhance your experience:
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                  <ListItemText 
                    primary="Review Template Generation" 
                    secondary="Creating customized audit templates based on your input"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                  <ListItemText 
                    primary="Review Insights" 
                    secondary="Analyzing audit responses to provide insights and recommendations"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                  <ListItemText 
                    primary="Sub-task Generation" 
                    secondary="Breaking down action items into manageable tasks"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                  <ListItemText 
                    primary="Company Research" 
                    secondary="Gathering publicly available information to enrich company profiles"
                  />
                </ListItem>
              </List>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                AI processing is performed using OpenRouter.ai. Your data is processed according to their 
                privacy policy and is not used to train AI models.
              </Typography>
            </SubSection>

            <SubSection title="3.3 Communication">
              <List dense>
                <ListItem>
                  <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                  <ListItemText primary="Send service-related notifications" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                  <ListItemText primary="Respond to your inquiries and support requests" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                  <ListItemText primary="Send important updates about the Service" />
                </ListItem>
              </List>
            </SubSection>

            <SubSection title="3.4 Improvement and Analytics">
              <List dense>
                <ListItem>
                  <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                  <ListItemText primary="Analyze usage patterns to improve the Service" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                  <ListItemText primary="Identify and fix technical issues" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                  <ListItemText primary="Develop new features and functionality" />
                </ListItem>
              </List>
            </SubSection>
          </Section>

          {/* Data Sharing */}
          <Section title="4. Data Sharing and Disclosure">
            <Typography paragraph>
              We do not sell your personal information. We may share your information in the following circumstances:
            </Typography>

            <SubSection title="4.1 Service Providers">
              <Typography paragraph>
                We work with trusted third-party service providers who assist us in operating our Service:
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><StorageIcon color="primary" /></ListItemIcon>
                  <ListItemText 
                    primary="Supabase" 
                    secondary="Database hosting and authentication (SOC 2 Type II certified)"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><StorageIcon color="primary" /></ListItemIcon>
                  <ListItemText 
                    primary="Stripe" 
                    secondary="Payment processing (PCI DSS compliant)"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><StorageIcon color="primary" /></ListItemIcon>
                  <ListItemText 
                    primary="OpenRouter.ai" 
                    secondary="AI processing for analysis and generation features"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><StorageIcon color="primary" /></ListItemIcon>
                  <ListItemText 
                    primary="Railway" 
                    secondary="Application hosting infrastructure"
                  />
                </ListItem>
              </List>
            </SubSection>

            <SubSection title="4.2 Within Your Organization">
              <Typography paragraph>
                If you are part of a consultant network or client company, your information may be visible 
                to other authorized users within your organization based on their role and permissions.
              </Typography>
            </SubSection>

            <SubSection title="4.3 Legal Requirements">
              <Typography paragraph>
                We may disclose your information if required by law, regulation, legal process, or 
                governmental request, or to protect the rights, property, or safety of our users or others.
              </Typography>
            </SubSection>

            <SubSection title="4.4 Business Transfers">
              <Typography paragraph>
                In the event of a merger, acquisition, or sale of assets, your information may be 
                transferred. We will provide notice before your information becomes subject to a 
                different privacy policy.
              </Typography>
            </SubSection>
          </Section>

          {/* Data Retention */}
          <Section title="5. Data Retention">
            <Typography paragraph>
              We retain your personal information for as long as necessary to provide the Service and 
              fulfill the purposes described in this policy, unless a longer retention period is required 
              or permitted by law.
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                <ListItemText 
                  primary="Active Accounts" 
                  secondary="Data is retained while your account is active"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                <ListItemText 
                  primary="Account Deletion" 
                  secondary="Upon request, personal data is deleted within 30 days"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                <ListItemText 
                  primary="Review Records" 
                  secondary="May be retained for legal/compliance purposes after account deletion"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                <ListItemText 
                  primary="Backup Data" 
                  secondary="Removed from backups within 90 days of deletion"
                />
              </ListItem>
            </List>
          </Section>

          {/* Your Rights */}
          <Section title="6. Your Rights">
            <Typography paragraph>
              You have the following rights regarding your personal information:
            </Typography>

            <SubSection title="6.1 Australian Privacy Principles (APPs)">
              <Typography paragraph>
                Under the Australian Privacy Act 1988, you have the right to:
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><GavelIcon color="primary" /></ListItemIcon>
                  <ListItemText 
                    primary="Access" 
                    secondary="Request access to the personal information we hold about you"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><GavelIcon color="primary" /></ListItemIcon>
                  <ListItemText 
                    primary="Correction" 
                    secondary="Request correction of inaccurate or incomplete information"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><GavelIcon color="primary" /></ListItemIcon>
                  <ListItemText 
                    primary="Complaint" 
                    secondary="Lodge a complaint with the Office of the Australian Information Commissioner (OAIC)"
                  />
                </ListItem>
              </List>
            </SubSection>

            <SubSection title="6.2 GDPR Rights (EU Residents)">
              <Typography paragraph>
                If you are located in the European Economic Area, you have additional rights:
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><GavelIcon color="primary" /></ListItemIcon>
                  <ListItemText 
                    primary="Right to Erasure" 
                    secondary="Request deletion of your personal data ('right to be forgotten')"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><GavelIcon color="primary" /></ListItemIcon>
                  <ListItemText 
                    primary="Right to Data Portability" 
                    secondary="Receive your data in a structured, machine-readable format"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><GavelIcon color="primary" /></ListItemIcon>
                  <ListItemText 
                    primary="Right to Restrict Processing" 
                    secondary="Request limitation of processing in certain circumstances"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><GavelIcon color="primary" /></ListItemIcon>
                  <ListItemText 
                    primary="Right to Object" 
                    secondary="Object to processing based on legitimate interests"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><GavelIcon color="primary" /></ListItemIcon>
                  <ListItemText 
                    primary="Right to Withdraw Consent" 
                    secondary="Withdraw consent at any time where processing is based on consent"
                  />
                </ListItem>
              </List>
            </SubSection>

            <SubSection title="6.3 Exercising Your Rights">
              <Typography paragraph>
                To exercise any of these rights, you can:
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                  <ListItemText primary="Use the 'My Data' section in your account settings" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                  <ListItemText primary={`Email us at ${contactEmail}`} />
                </ListItem>
              </List>
              <Typography variant="body2" color="text.secondary">
                We will respond to your request within 30 days. We may need to verify your identity 
                before processing certain requests.
              </Typography>
            </SubSection>
          </Section>

          {/* Data Security */}
          <Section title="7. Data Security">
            <Typography paragraph>
              We implement appropriate technical and organizational measures to protect your personal 
              information:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon><SecurityIcon color="primary" /></ListItemIcon>
                <ListItemText 
                  primary="Encryption" 
                  secondary="All data is encrypted in transit (TLS 1.3) and at rest (AES-256)"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><SecurityIcon color="primary" /></ListItemIcon>
                <ListItemText 
                  primary="Access Controls" 
                  secondary="Role-based access with multi-tenant data isolation"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><SecurityIcon color="primary" /></ListItemIcon>
                <ListItemText 
                  primary="Infrastructure" 
                  secondary="SOC 2 Type II certified hosting providers"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><SecurityIcon color="primary" /></ListItemIcon>
                <ListItemText 
                  primary="Monitoring" 
                  secondary="Continuous security monitoring and regular audits"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><SecurityIcon color="primary" /></ListItemIcon>
                <ListItemText 
                  primary="Backups" 
                  secondary="Regular automated backups with point-in-time recovery"
                />
              </ListItem>
            </List>
          </Section>

          {/* International Transfers */}
          <Section title="8. International Data Transfers">
            <Typography paragraph>
              Your information may be transferred to and processed in countries other than Australia, 
              including the United States where our service providers operate.
            </Typography>
            <Typography paragraph>
              When we transfer data internationally, we ensure appropriate safeguards are in place, 
              including:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                <ListItemText primary="Standard Contractual Clauses approved by relevant authorities" />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                <ListItemText primary="Binding Corporate Rules where applicable" />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                <ListItemText primary="Adequacy decisions for transfers to approved countries" />
              </ListItem>
            </List>
          </Section>

          {/* Children's Privacy */}
          <Section title="9. Children's Privacy">
            <Typography paragraph>
              Our Service is not intended for children under 16 years of age. We do not knowingly 
              collect personal information from children. If you become aware that a child has 
              provided us with personal information, please contact us and we will take steps to 
              delete such information.
            </Typography>
          </Section>

          {/* Changes to Policy */}
          <Section title="10. Changes to This Policy">
            <Typography paragraph>
              We may update this Privacy Policy from time to time. We will notify you of any material 
              changes by:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                <ListItemText primary="Posting the updated policy on this page" />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                <ListItemText primary="Updating the 'Last updated' date" />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                <ListItemText primary="Sending an email notification for significant changes" />
              </ListItem>
            </List>
            <Typography paragraph>
              We encourage you to review this policy periodically for any changes.
            </Typography>
          </Section>

          {/* Contact */}
          <Section title="11. Contact Us">
            <Typography paragraph>
              If you have any questions about this Privacy Policy or our data practices, please contact us:
            </Typography>
            <Box sx={{ 
              p: 3, 
              bgcolor: 'grey.50', 
              borderRadius: 2, 
              border: '1px solid',
              borderColor: 'grey.200'
            }}>
              <Typography variant="h6" sx={{ mb: 2 }}>{companyName}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <EmailIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Link href={`mailto:${contactEmail}`}>{contactEmail}</Link>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                For privacy complaints, you may also contact the Office of the Australian Information 
                Commissioner (OAIC) at{' '}
                <Link href="https://www.oaic.gov.au" target="_blank" rel="noopener">
                  www.oaic.gov.au
                </Link>
              </Typography>
            </Box>
          </Section>

          <Divider sx={{ my: 4 }} />

          {/* Footer */}
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              © {new Date().getFullYear()} {companyName}. All rights reserved.
            </Typography>
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 3 }}>
              <Link href="/terms-of-service" color="primary">Terms of Service</Link>
              <Link href="/cookie-policy" color="primary">Cookie Policy</Link>
            </Box>
          </Box>
        </Paper>
    </Container>
  );
};

export default PrivacyPolicy;

